import React, { useEffect, useRef } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Barcode from 'react-barcode';
import Loader from '../App/layout/Loader';
import Config from '../config';
import { GATE_PASS } from '../store/programs/GatePass/gatePass.reducer';
import { NAVIGATE_GATE_PASS_MASTER } from '../store/programs/GatePass/gatePass.actionsType';
import { fetchGatePassDetails } from '../store/programs/GatePass/gatePassDetails.actions';
import { authorizeGatePass } from '../store/programs/GatePass/gatePass.actions';
import Swal from 'sweetalert2';

// Styles for the printed sheet. Used on screen (preview) and copied into the
// print window, so what you see is what prints. A4 landscape.
const PRINT_CSS = `
.gp-sheet { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; background: #fff; max-width: 1080px; margin: 0 auto; border: 1px solid #000; }
.gp-sheet * { box-sizing: border-box; }
.gp-head { text-align: center; padding: 8px; border-bottom: 1px solid #000; }
.gp-head .gp-company { font-size: 18px; font-weight: bold; text-transform: uppercase; }
.gp-head .gp-addr { font-size: 11px; }
.gp-title { text-align: center; font-weight: bold; font-size: 14px; padding: 5px; border-bottom: 1px solid #000; letter-spacing: 1px; }
.gp-row { display: flex; border-bottom: 1px solid #000; }
.gp-cell { flex: 1; padding: 6px 8px; }
.gp-cell + .gp-cell { border-left: 1px solid #000; }
.gp-barcode { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; padding: 4px 8px; }
.gp-barcode svg { display: block; max-width: 260px; height: auto; }
.gp-label { font-weight: bold; font-size: 11px; text-transform: uppercase; margin-bottom: 2px; }
.gp-kv { display: flex; margin: 1px 0; }
.gp-kv span:first-child { width: 110px; font-weight: bold; flex-shrink: 0; }
.gp-table { width: 100%; border-collapse: collapse; }
.gp-table th, .gp-table td { border: 1px solid #000; padding: 4px 5px; font-size: 11px; }
.gp-table th { background: #eee; text-align: center; }
.gp-table td.num { text-align: right; }
.gp-table tfoot td { font-weight: bold; }
.gp-remarks { padding: 6px 8px; border-top: 1px solid #000; min-height: 34px; }
.gp-sign { display: flex; border-top: 1px solid #000; }
.gp-sign > div { flex: 1; text-align: center; padding: 6px 5px; font-size: 11px; min-height: 90px; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; }
.gp-sign > div + div { border-left: 1px solid #000; }
.gp-sign img { max-height: 45px; max-width: 150px; object-fit: contain; margin-bottom: 2px; }
.gp-sign .gp-sign-name { font-size: 11px; min-height: 14px; }
.gp-sign .gp-sign-label { font-weight: bold; border-top: 1px dotted #000; padding-top: 2px; margin-top: 2px; min-width: 70%; }
@media print {
  @page { size: A4 landscape; margin: 8mm; }
  body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .gp-sheet { max-width: none; }
}
`;

const n = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v));
const fmt2 = (v) => n(v).toFixed(2);
const fmtDate = (d) => (d ? moment(d).format('DD-MM-YYYY') : '');
const joinAddr = (...parts) => parts.filter((p) => p && String(p).trim()).map((p) => String(p).trim().replace(/,$/, '')).join(', ');

// Signature can come as a full data URI, a URL, a server path, or plain base64.
const signSrc = (v) => {
    if (!v || typeof v !== 'string') return null;
    const s = v.trim();
    if (!s) return null;
    if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) return s;
    if (s.startsWith('/')) return `${Config.baseUrl}${s}`;
    if (/^[A-Za-z0-9+/=\r\n]+$/.test(s) && s.length > 100) return `data:image/png;base64,${s}`;
    return `${Config.baseUrl}/${s}`;
};

// Detail rows from the API may be PascalCase — read either spelling.
const pick = (row, key) => row?.[key] ?? row?.[key.charAt(0).toUpperCase() + key.slice(1)];

const GatePassPrint = () => {
    const dispatch = useDispatch();
    const sheetRef = useRef();
    const { selectedGatePass, gatePassDetails, detailsLoading, loading } = useSelector((state) => state[GATE_PASS]);
    const gp = selectedGatePass || {};
    const isAuthorized = !!(gp.fldIsAuthorized ?? gp.FldIsAuthorized);

    const handleAuthorize = () => {
        Swal.fire({
            title: `Authorize Gate Pass ${gp.fldGatePassNo || ''}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, authorize'
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            const ok = await dispatch(authorizeGatePass(gp.fldId));
            // reload details so the authorized name / signature show on the sheet
            if (ok) dispatch(fetchGatePassDetails(gp.fldId));
        });
    };

    useEffect(() => {
        dispatch(fetchGatePassDetails(gp.fldId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gp.fldId]);

    const rows = gatePassDetails || [];
    const totalQty = rows.reduce((s, r) => s + n(pick(r, 'fldQuantity')), 0);
    const totalTaxable = rows.reduce((s, r) => s + n(pick(r, 'fldTaxableValue')), 0);
    const totalGst = rows.reduce((s, r) => s + n(pick(r, 'fldGSTValue')), 0);
    const totalValue = rows.reduce((s, r) => s + n(pick(r, 'fldTotalValue')), 0);

    // Print only the sheet (not the menu / header of the app) by copying it
    // into a new window with its own styles.
    const handlePrint = () => {
        const html = sheetRef.current?.outerHTML;
        if (!html) return;
        const win = window.open('', '_blank', 'width=1200,height=850');
        if (!win) {
            window.print(); // pop-up blocked — fall back to printing the page
            return;
        }
        win.document.write(
            `<!doctype html><html><head><title>Gate Pass ${gp.fldGatePassNo || ''}</title><style>${PRINT_CSS}</style></head><body>${html}</body></html>`
        );
        win.document.close();
        win.focus();
        // Wait for the signature images to load, then print.
        const doPrint = () => {
            win.print();
            win.close();
        };
        const imgs = Array.from(win.document.images || []);
        const pending = imgs.filter((img) => !img.complete);
        if (pending.length === 0) {
            setTimeout(doPrint, 300);
        } else {
            let left = pending.length;
            const done = () => {
                left -= 1;
                if (left <= 0) setTimeout(doPrint, 200);
            };
            pending.forEach((img) => {
                img.onload = done;
                img.onerror = done;
            });
            setTimeout(doPrint, 4000); // safety net if an image never loads
        }
    };

    const title = gp.fldIsReturnable ? 'RETURNABLE GATE PASS' : 'NON-RETURNABLE GATE PASS';

    return (
        <Row className="d-flex flex-column">
            {detailsLoading && <Loader />}
            <style>{PRINT_CSS}</style>
            <Col>
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <Button variant="primary" size="sm" onClick={() => dispatch({ type: NAVIGATE_GATE_PASS_MASTER })}>
                            BACK
                        </Button>
                        <Card.Title as="h5" className="m-0" style={{ color: 'blue', fontWeight: 'bold' }}>
                            Gate Pass Print — {gp.fldGatePassNo || ''}
                        </Card.Title>
                        <div className="d-flex">
                            <Button
                                size="sm"
                                // same green style as the Authorize / Authorized buttons on the other DC screens
                                className="btn-green text-white"
                                style={{ marginRight: 8 }}
                                onClick={handleAuthorize}
                                disabled={isAuthorized || loading}
                            >
                                {isAuthorized ? 'Authorized' : 'Authorize'}
                            </Button>
                            <Button variant="success" size="sm" onClick={handlePrint} disabled={detailsLoading}>
                                <i className="feather icon-printer" style={{ marginRight: 5 }} />
                                PRINT
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body style={{ background: '#f4f4f4' }}>
                        <div className="gp-sheet" ref={sheetRef}>
                            {/* Company / Unit */}
                            <div className="gp-head">
                                <div className="gp-company">{gp.fldUnitName || ''}</div>
                                <div className="gp-addr">
                                    {joinAddr(gp.fldUnitAddress1, gp.fldUnitAddress2, gp.fldUnitArea, gp.fldUnitCity, gp.fldUnitState)}
                                    {gp.fldUnitPincode ? ` - ${gp.fldUnitPincode}` : ''}
                                </div>
                                {gp.fldUnitGSTNo && <div className="gp-addr">GSTIN: {gp.fldUnitGSTNo}</div>}
                            </div>
                            <div className="gp-title">{title}</div>

                            {/* Gate pass info */}
                            <div className="gp-row">
                                <div className="gp-cell">
                                    <div className="gp-kv"><span>Gate Pass No</span><span>: {gp.fldGatePassNo || ''}</span></div>
                                    <div className="gp-kv"><span>Date</span><span>: {fmtDate(gp.fldGatePassDate)}</span></div>
                                    <div className="gp-kv"><span>Vehicle No</span><span>: {gp.fldVehicleNO || ''}</span></div>
                                    {/* <div className="gp-kv"><span>Purpose</span><span>: {gp.fldPurpose || ''}</span></div> */}
                                </div>
                                <div className="gp-cell">
                                    <div className="gp-kv"><span>Style</span><span>: {gp.fldMainStyle || gp.fldStyleNumber || ''}{gp.fldStyle ? ` / ${gp.fldStyle}` : ''}</span></div>
                                    {/* <div className="gp-kv"><span>Sales Order</span><span>: {gp.fldSaleOrder || gp.fldSaleOrderNumber || ''}</span></div> */}
                                    <div className="gp-kv"><span>Delivery Date</span><span>: {fmtDate(gp.fldDeliveryStartDate)}</span></div>
                                    {gp.fldIsReturnable && (
                                        <div className="gp-kv"><span>Return By</span><span>: {gp.fldReturnBy || ''} {fmtDate(gp.fldReturnDate)}</span></div>
                                    )}
                                </div>
                                {/* Barcode of the Gate Pass No — security can scan it at the gate */}
                                {gp.fldGatePassNo && (
                                    <div className="gp-cell gp-barcode">
                                        <Barcode
                                            value={String(gp.fldGatePassNo)}
                                            format="CODE128"
                                            renderer="svg"
                                            width={1.2}
                                            height={45}
                                            fontSize={12}
                                            margin={0}
                                            displayValue={true}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* From / To */}
                            {/* <div className="gp-row">
                                <div className="gp-cell">
                                    <div className="gp-label">Supplier</div>
                                    <div style={{ fontWeight: 'bold' }}>{gp.fldSupplierName || ''}</div>
                                    <div>{joinAddr(gp.fldSupplierAddress1, gp.fldSupplierAddress2, gp.fldSupplierArea, gp.fldSupplierCity, gp.fldSupplierState)}{gp.fldSupplierPincode ? ` - ${gp.fldSupplierPincode}` : ''}</div>
                                    {gp.fldSupplierPhoneNo && <div>Phone: {gp.fldSupplierPhoneNo}</div>}
                                    {gp.fldSupplierGSTNo && <div>GSTIN: {gp.fldSupplierGSTNo}</div>}
                                </div>
                                <div className="gp-cell">
                                    <div className="gp-label">Delivery At</div>
                                    <div style={{ fontWeight: 'bold' }}>{gp.fldDeliveryAtName || ''}</div>
                                    <div>{joinAddr(gp.fldDeliveryAtAddress1, gp.fldDeliveryAtAddress2, gp.fldDeliveryAtArea, gp.fldDeliveryAtCity, gp.fldDeliveryAtState)}{gp.fldDeliveryAtPincode ? ` - ${gp.fldDeliveryAtPincode}` : ''}</div>
                                    {gp.fldDeliveryPhoneNo && <div>Phone: {gp.fldDeliveryPhoneNo}</div>}
                                    {gp.fldDeliveryAtGSTNo && <div>GSTIN: {gp.fldDeliveryAtGSTNo}</div>}
                                </div>
                            </div> */}

                            {/* Items */}
                            <table className="gp-table">
                                <thead>
                                    <tr>
                                        <th>S.No</th>
                                        <th>Item</th>
                                        <th>Description</th>
                                        {/* <th>HSN</th> */}
                                        <th>Colour</th>
                                        <th>Size</th>
                                        <th>UOM</th>
                                        <th>Qty</th>
                                        {/* <th>Rate</th>
                                        <th>Taxable</th>
                                        <th>GST</th>
                                        <th>Total</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={12} style={{ textAlign: 'center', padding: 12 }}>
                                                {detailsLoading ? 'Loading…' : 'No items added to this Gate Pass'}
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((r, i) => (
                                            <tr key={pick(r, 'fldId') ?? i}>
                                                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                                                <td>{[pick(r, 'fldGeneralHeader'), pick(r, 'fldGeneralItem')].filter(Boolean).join(' - ')}</td>
                                                <td>{pick(r, 'fldItemDescription') || ''}</td>
                                                {/* <td>{r?.tblHSNCode?.fldSalesCode || pick(r, 'fldHSNCode') || ''}</td> */}
                                                <td>{r?.tblColourDetailId?.fldDescription || pick(r, 'fldColour') || ''}</td>
                                                <td>{pick(r, 'fldSize') || ''}</td>
                                                <td>{r?.tblUOM?.fldDescription || pick(r, 'fldUOM') || ''}</td>
                                                <td className="num">{n(pick(r, 'fldQuantity'))}</td>
                                                {/* <td className="num">{fmt2(pick(r, 'fldRate'))}</td>
                                                <td className="num">{fmt2(pick(r, 'fldTaxableValue'))}</td>
                                                <td className="num">{fmt2(pick(r, 'fldGSTValue'))}</td>
                                                <td className="num">{fmt2(pick(r, 'fldTotalValue'))}</td> */}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={6} style={{ textAlign: 'right' }}>Total</td>
                                        <td className="num">{totalQty}</td>
                                        {/* <td />
                                        <td className="num">{fmt2(totalTaxable)}</td>
                                        <td className="num">{fmt2(totalGst)}</td>
                                        <td className="num">{fmt2(totalValue)}</td> */}
                                    </tr>
                                </tfoot>
                            </table>

                            <div className="gp-remarks">
                                <b>Remarks:</b> {gp.fldRemarks || ''}
                            </div>
                            <div className="gp-row" style={{ borderTop: '1px solid #000', borderBottom: 0 }}>
                                <div className="gp-cell">
                                    <div className="gp-kv"><span>Taken By</span><span>: {gp.fldTakenBy || ''}</span></div>
                                </div>
                                <div className="gp-cell">
                                    <div className="gp-kv"><span>Taken Date</span><span>: {fmtDate(gp.fldTakenDate)}</span></div>
                                </div>
                            </div>
                            <div className="gp-sign">
                                <div>
                                    {signSrc(rows[0]?.fldPreparedSign) && <img src={signSrc(rows[0]?.fldPreparedSign)} alt="Prepared signature" />}
                                    <div className="gp-sign-name">{rows[0]?.fldPreparedBy || ''}</div>
                                    <div className="gp-sign-label">Prepared By</div>
                                </div>
                                <div>
                                    {signSrc(rows[0]?.fldAuthorizedSign) && <img src={signSrc(rows[0]?.fldAuthorizedSign)} alt="Authorised signature" />}
                                    <div className="gp-sign-name">{rows[0]?.fldAuthorizedBy || ''}</div>
                                    <div className="gp-sign-label">Authorised Signatory</div>
                                </div>
                                <div>
                                    <div className="gp-sign-label">Receiver's Signature</div>
                                </div>
                                <div>
                                    <div className="gp-sign-label">Security</div>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default GatePassPrint;
